import { useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Shared Delete flow for every Payment Desk record (UPI, Cash, RTGS/NEFT lines):
// shows the key details, enables Delete only after CONFIRM is typed exactly.
export function DeleteEntryDialog({ title, details, endpoint, onClose, onDeleted }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (text !== "CONFIRM") return;
    setBusy(true);
    try {
      await client.post(endpoint, { confirm: text });
      toast.success("Entry deleted");
      onDeleted();
    } catch (err) {
      toast.error(getErrorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="delete-entry-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>This hides the entry from every screen. It stays on record in Deleted Entries and cannot be restored.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <dl className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm space-y-1" data-testid="delete-entry-details">
            {details.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4">
                <dt className="text-slate-500">{k}</dt>
                <dd className="font-medium text-right">{v || "—"}</dd>
              </div>
            ))}
          </dl>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Type CONFIRM to delete</label>
            <Input data-testid="delete-entry-confirm-input" autoFocus value={text} onChange={(e) => setText(e.target.value)} autoComplete="off" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy} data-testid="delete-entry-cancel-button">Cancel</Button>
            <Button type="submit" variant="destructive" disabled={busy || text !== "CONFIRM"} data-testid="delete-entry-submit-button">
              {busy ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
